import React from 'react';
import { Plan, PricingTier, BillingCycle, Feature } from './types';
import { BrainCircuit, Merge, Lightbulb, Search, Crosshair, Zap } from 'lucide-react';

export interface NavLink {
  name: string;
  href: string;
}

export type { Feature };

export const NAV_LINKS: NavLink[] = [
  { name: 'Home', href: '/' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'Contact', href: '/contact' },
];

export const FEATURES: Feature[] = [
  {
    name: 'Dynamic Branching',
    description: 'Never lose a tangent. Branch your conversation from any idea instantly to explore thoughts without cluttering the main flow.',
    icon: <Zap className="w-8 h-8 text-emerald-500" />,
  },
  {
    name: 'AI-Powered Merging',
    description: 'Bring it all together. Our AI summarizes and seamlessly integrates your branched discussions back into the main conversation.',
    icon: <Merge className="w-8 h-8 text-emerald-500" />,
  },
  {
    name: 'Cross-Branch Synthesis',
    description: 'Connect the dots. Ask your entire thought tree questions and get synthesized insights from multiple branches at once.',
    icon: <BrainCircuit className="w-8 h-8 text-emerald-500" />,
  },
  {
    name: 'Proactive Insights',
    description: 'Our AI acts as an overseer, detecting potential conflicts or synergies between branches to guide your brainstorming.',
    icon: <Lightbulb className="w-8 h-8 text-emerald-500" />,
  },
   {
    name: 'Seamless Context Control',
    description: 'Choose exactly how context is passed to new branches—full history, an AI-summary, or a completely fresh start.',
    icon: <Search className="w-8 h-8 text-emerald-500" />,
  },
  {
    name: 'Branch Focus View',
    description: 'Eliminate distractions. Subtly fade out the rest of the tree to concentrate on a single, isolated conversation.',
    icon: <Crosshair className="w-8 h-8 text-emerald-500" />,
  },
];


export const PRICING_PLANS: Plan[] = [
  {
    name: PricingTier.Free,
    price: {
        [BillingCycle.Monthly]: '$0',
        [BillingCycle.Annually]: '$0',
    },
    description: 'Perfect for getting started and trying out ThreadIt.',
    features: [
      '100 chat messages per month',
      'Unlimited conversational messages per note',
      '3 Thread Starters (main ideas)',
      '5 Thread Ideas per starter',
      '20 total notes per canvas',
      '3 file attachments per canvas',
      '3 merge operations per canvas',
      '3 web searches per canvas',
      'Community Support',
    ],
    isFeatured: false,
  },
  {
    name: PricingTier.Basic,
    price: {
      [BillingCycle.Monthly]: '$18',
      [BillingCycle.Annually]: '$15',
    },
    description: 'For users who need continuous access to standard brainstorming tools.',
    features: [
      '1,200 chat messages per month',
      'Unlimited conversational messages per note',
      '10 Thread Starters (main ideas)',
      '10 Thread Ideas per starter',
      'Unlimited total notes',
      '10 file attachments per canvas',
      '15 merge operations per canvas',
      '20 web searches per canvas',
      'Priority Email Support',
    ],
    isFeatured: true,
  },
  {
    name: PricingTier.Pro,
    price: {
      [BillingCycle.Monthly]: '$25',
      [BillingCycle.Annually]: '$20',
    },
    description: 'For power users who need the most advanced AI capabilities.',
    features: [
      'All Basic features',
      'Unlimited chat messages per month',
      'Unlimited Thread Starters',
      'Unlimited Thread Ideas',
      'Unlimited file attachments',
      'Unlimited merge operations',
      'Unlimited web searches',
      'Deep thinking models',
      'Cross-Branch Synthesis',
      'Proactive Conflict Detection',
      'Dedicated Support',
    ],
    isFeatured: false,
  },
];